import { useCallback, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";

const LS_KEY = "mannaLeftNavCollapsed";
const prefsKey = ["user_ui_preferences"] as const;

interface LeftNavPrefs {
  left_nav_collapsed: boolean | null;
}

function readLocal(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(LS_KEY) === "1";
}

function usePrefs() {
  return useQuery({
    queryKey: prefsKey,
    queryFn: async (): Promise<LeftNavPrefs | null> => {
      const { data, error } = await supabase
        .from("user_ui_preferences_view")
        .select("left_nav_collapsed")
        .maybeSingle();
      if (error) throw error;
      return data as LeftNavPrefs | null;
    },
    staleTime: 5 * 60_000,
  });
}

function useUpsert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<LeftNavPrefs>) => {
      const { error } = await supabase.rpc("upsert_user_ui_preferences", {
        payload: payload as never,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: prefsKey }),
  });
}

export function useLeftNavCollapsed() {
  const { data } = usePrefs();
  const upsert = useUpsert();
  const [override, setOverride] = useState<boolean | null>(null);

  const collapsed =
    override ??
    (typeof data?.left_nav_collapsed === "boolean" ? data.left_nav_collapsed : readLocal());

  const setCollapsed = useCallback(
    (next: boolean) => {
      setOverride(next);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(LS_KEY, next ? "1" : "0");
      }
      upsert.mutate({ left_nav_collapsed: next });
    },
    [upsert],
  );

  return { collapsed, setCollapsed, toggle: () => setCollapsed(!collapsed) };
}
